import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Sloučeno z landingu a z ``/for-providers`` (KAN-908). Union, ne
// výměna: obě sady měly něco, co ta druhá neuměla říct. ``geometry``
// a ``capacity`` nesou to, co konkurence nemá — cena a výroba čtou
// tatáž data — a prostá náhrada šesti kartami z ``for-providers`` by
// je zahodila. ``noIt`` („žádný programátor") vypadl jinak: totéž
// dnes říká hero podtitulek (KAN-903) i FAQ „Nejsem ajťák. Rozjedu
// to?", takže karta byla třetí kopie. ``b2b`` zůstal na
// ``/for-business``, kam patří publikem.
const FEATURE_KEYS = [
  "pricing",
  "geometry",
  "capacity",
  "branding",
  "production",
  "compliance",
] as const;

const ICONS: Record<(typeof FEATURE_KEYS)[number], ReactNode> = {
  pricing: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  ),
  geometry: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  capacity: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  branding: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  production: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 20V9l5 3V9l5 3V9l5 3V20z" />
      <line x1="3" y1="20" x2="21" y2="20" />
      <line x1="18" y1="9" x2="18" y2="4" />
    </svg>
  ),
  compliance: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <polyline points="9 15 11 17 15 13" />
    </svg>
  ),
};

const MISSING_ICON: ReactNode = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function PlatformFeatures(): JSX.Element {
  const { t } = useTranslation("common");

  return (
    <section className="section">
      <div className="inner">
        <p className="kicker">{t("platformLanding.features.kicker")}</p>
        <h2 className="h2">{t("platformLanding.features.heading")}</h2>

        <ul className="feats" data-testid="platform-features">
          {FEATURE_KEYS.map((key) => (
            <li key={key} className="feat">
              <div className="feat-icon">{ICONS[key]}</div>
              <h3>{t(`platformLanding.features.items.${key}.title`)}</h3>
              <p>{t(`platformLanding.features.items.${key}.body`)}</p>
            </li>
          ))}
          {/* Full-width invitation under the six features to ask for what
              isn't listed. Targets the contact form rendered further down
              the same page. */}
          <li className="feat cta">
            <div className="txt">
              <div className="feat-icon">{MISSING_ICON}</div>
              <h3>{t("platformLanding.features.missing.title")}</h3>
              <p>{t("platformLanding.features.missing.body")}</p>
            </div>
            <a
              href="#kontakt"
              className="inline-link"
              data-testid="platform-features-missing"
            >
              {t("platformLanding.features.missing.cta")}
              <span aria-hidden="true">→</span>
            </a>
          </li>
        </ul>
      </div>
    </section>
  );
}
